import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box, Container, Typography, Card, CardContent,
  Divider, Grid, CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import { msgTypes } from "../../constants/msgTypes";
import "./ResponsePage.css";

const PAYMENT_TYPE_LABELS = {
  UP: "UPI",
  CC: "Credit Card",
  DC: "Debit Card",
  NB: "Net Banking",
  WL: "Wallet",
};

const ResponsePage = () => {
  const location = useLocation();

  const [response, setResponse]       = useState({});
  const [isLoaded, setIsLoaded]       = useState(false);
  const [countdown, setCountdown]     = useState(10);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // ── Load response from state or URL params ─────────────────
  useEffect(() => {
    // Priority 1: router state (from polling navigate)
    if (location?.state?.response) {
      setResponse(location.state.response);
      setIsLoaded(true);
      return;
    }
    // Priority 2: URL query params (from bank redirect)
    if (location?.search) {
      const params = new URLSearchParams(location.search);
      if (params.size > 0) {
        const result = {};
        params.forEach((value, key) => {
          result[key] = value === "null" ? null : decodeURIComponent(value);
        });
        setResponse(result);
        setIsLoaded(true);
      }
    }
  }, [location?.state, location?.search]);

  // ── Auto-redirect countdown ────────────────────────────────
  useEffect(() => {
    const isTerminal = response.status === msgTypes.SUCCESS || response.status === msgTypes.FAILED;
    if (!response.returnUrl || !isLoaded || !isTerminal) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsRedirecting(true);
          setTimeout(() => { window.location.href = response.returnUrl; }, 500);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [response.returnUrl, response.status, isLoaded]);

  // ── Helpers ────────────────────────────────────────────────
  const getStatusColor = () => {
    if (response.status === msgTypes.SUCCESS) return "#22c55e";
    if (response.status === msgTypes.FAILED)  return "#ef4444";
    if (response.status === msgTypes.PENDING) return "#f59e0b";
    return "#6b7280";
  };

  const getStatusTitle = () => {
    if (response.status === msgTypes.SUCCESS) return "Payment Successful!";
    if (response.status === msgTypes.FAILED)  return "Payment Failed";
    if (response.status === msgTypes.PENDING) return "Payment Pending";
    return "Payment Status";
  };

  const getStatusMessage = () => {
    if (response.status === msgTypes.SUCCESS) return "Your payment has been processed successfully.";
    if (response.status === msgTypes.FAILED)  return "Unfortunately, your payment could not be processed.";
    if (response.status === msgTypes.PENDING) return "Your payment is being processed. Please wait.";
    return response.responseMessage || "Processing your transaction…";
  };

  const StatusIcon = () => {
    if (response.status === msgTypes.SUCCESS) return <CheckCircleIcon className="status-icon success-icon" sx={{ fontSize: "120px" }} />;
    if (response.status === msgTypes.FAILED)  return <CancelIcon      className="status-icon failed-icon"  sx={{ fontSize: "120px" }} />;
    if (response.status === msgTypes.PENDING) return <PendingIcon     className="status-icon pending-icon" sx={{ fontSize: "120px" }} />;
    return null;
  };

  // ── Transaction detail rows ────────────────────────────────
  const DETAIL_ROWS = [
    { label: "Transaction ID",  value: response.txnId },
    { label: "Order ID",        value: response.orderId },
    { label: "Amount",          value: response.amount ? `₹ ${response.amount}` : null },
    { label: "Status",          value: response.status,  color: getStatusColor() },
    { label: "UTR Number",      value: response.utr },
    { label: "Email",           value: response.custEmail },
    { label: "Phone",           value: response.custPhone },
    {
      label: "Payment Method",
      value: response.paymentTypeCode
        ? (PAYMENT_TYPE_LABELS[response.paymentTypeCode] ?? response.paymentTypeCode)
        : null,
    },
    { label: "Message",         value: response.responseMessage, fullWidth: true },
  ].filter((r) => r.value);

  // ─────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Container maxWidth="md">
        <Card
          className="response-card"
          sx={{ borderRadius: "24px", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 6 }, textAlign: "center" }}>

            {/* ── Loading state ── */}
            {!isLoaded || !response.status ? (
              <Box>
                <CircularProgress size={80} sx={{ color: "#667eea", mb: 3 }} />
                <Typography variant="h5" fontWeight={600} color="#1f2937" mb={1}>
                  Processing Your Transaction
                </Typography>
                <Typography variant="body1" color="#6b7280">
                  Please wait while we retrieve your payment status…
                </Typography>
              </Box>
            ) : (
              <>
                {/* Status icon */}
                <Box mb={3}><StatusIcon /></Box>

                {/* Title */}
                <Typography
                  variant="h4"
                  fontWeight={700}
                  color={getStatusColor()}
                  mb={2}
                  sx={{ fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" } }}
                >
                  {getStatusTitle()}
                </Typography>

                {/* Message */}
                <Typography variant="body1" color="#6b7280" mb={4}>
                  {getStatusMessage()}
                </Typography>

                <Divider sx={{ mb: 4 }} />

                {/* Transaction details */}
                <Box
                  sx={{
                    textAlign: "left",
                    background: "#f9fafb",
                    borderRadius: "16px",
                    p: { xs: 2, sm: 3 },
                    mb: 4,
                  }}
                >
                  <Typography variant="h6" fontWeight={600} color="#1f2937" mb={2} fontSize={{ xs: "1rem", sm: "1.1rem" }}>
                    Transaction Details
                  </Typography>
                  <Grid container spacing={2}>
                    {DETAIL_ROWS.map(({ label, value, color, fullWidth }) => (
                      <Grid item xs={12} sm={fullWidth ? 12 : 6} key={label}>
                        <Typography variant="caption" color="#6b7280" display="block" mb={0.5}>
                          {label}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={color || "#1f2937"}
                          sx={{ textTransform: label === "Status" ? "uppercase" : "none" }}
                        >
                          {value}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {/* Redirect info */}
                {response.returnUrl && (
                  <Box mt={3} mb={2} textAlign="center">
                    <Typography variant="body2" color="#6b7280" mb={0.5}>
                      You will be redirected automatically. If not,{" "}
                      <Box
                        component="a"
                        href={response.returnUrl}
                        sx={{ color: "#3b82f6", fontWeight: 600, textDecoration: "underline", cursor: "pointer", "&:hover": { color: "#2563eb" } }}
                      >
                        click here
                      </Box>
                    </Typography>
                  </Box>
                )}

                {!response.returnUrl && (
                  <Box mt={3} mb={2} p={2} sx={{ background: "#fff3cd", borderRadius: "12px" }}>
                    <Typography variant="body2" color="#856404">
                      Note: Return URL not available in transaction response.
                    </Typography>
                  </Box>
                )}

                {/* Countdown bar */}
                {response.returnUrl &&
                  !isRedirecting &&
                  (response.status === msgTypes.SUCCESS || response.status === msgTypes.FAILED) && (
                    <Box sx={{ background: "#eff6ff", borderRadius: "12px", p: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                      <CircularProgress size={24} sx={{ color: "#3b82f6" }} variant="determinate" value={(countdown / 10) * 100} />
                      <Typography variant="body2" color="#1e40af" fontWeight={500}>
                        Redirecting in {countdown} second{countdown !== 1 ? "s" : ""}…
                      </Typography>
                    </Box>
                  )}

                {isRedirecting && (
                  <Box sx={{ background: "#eff6ff", borderRadius: "12px", p: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                    <CircularProgress size={24} sx={{ color: "#3b82f6" }} />
                    <Typography variant="body2" color="#1e40af" fontWeight={500}>Redirecting now…</Typography>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ResponsePage;
